import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface FreshPermittingRecord {
  Place: string;
  place_fips: string;
  year: number | string;
  bps_total_units: number;
}

export function useFreshPermittingData(
  place: string | null,
  placeFips: string | null,
  targetYear: number = 2024
) {
  const [data, setData] = useState<FreshPermittingRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!place || !placeFips) {
      setLoading(false);
      setData(null);
      setError(null);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      // First: try specific target year
      const { data: yearData, error: yearError } = await supabase
        .from('acs_bps_metrics_fresh_permitting')
        .select('Place, place_fips, year, bps_total_units')
        .eq('Place', place)
        .eq('place_fips', placeFips)
        .eq('year', targetYear)
        .maybeSingle();

      if (yearError && yearError.code !== 'PGRST116') {
        // A real error occurred (not just no rows)
        console.error('Error fetching fresh permitting data for target year:', yearError);
        setError(yearError.message);
        setData(null);
        setLoading(false);
        return;
      }

      if (yearData) {
        console.log('FRESH permits (target year):', yearData);
        setData(yearData as FreshPermittingRecord);
        setLoading(false);
        return;
      }

      // Fallback: fetch latest available year
      const { data: latestRows, error: latestError } = await supabase
        .from('acs_bps_metrics_fresh_permitting')
        .select('Place, place_fips, year, bps_total_units')
        .eq('Place', place)
        .eq('place_fips', placeFips)
        .order('year', { ascending: false })
        .limit(1);

      if (latestError) {
        console.error('Error fetching fallback fresh permitting data:', latestError);
        setError(latestError.message);
        setData(null);
        setLoading(false);
        return;
      }

      const latestData = (latestRows ?? [])[0] as FreshPermittingRecord | undefined;
      if (latestData) {
        console.log('FRESH permits (fallback latest year):', latestData);
      } else {
        console.log('FRESH permits: no data available');
      }
      setData(latestData ?? null);
      setLoading(false);
    };

    fetchData();
  }, [place, placeFips, targetYear]);

  return { data, loading, error };
} 